// ════════════════════════════════════════════════════════════════════════════
//  ManDana — the intervals of a day
//  ---------------------------------------------------------------------------
//  The Schedule view places database rows BETWEEN prayers, and this module is
//  the whole of what "between" means: items in, intervals out. Pure — no DOM,
//  no store, no adhan — so the contract is provable without a browser or a
//  location (tests/schedule.test.js). The items are exactly what mdDayItems
//  returns, which is the same source the day view draws from, so Schedule can
//  never disagree with Today about the shape of a day.
//
//  THE FOUNDING LAW, APPLIED. Prayer is the axis, never a target. An interval
//  is named by the prayer that OPENS it — "the stretch after Asr" — and the
//  prayer's own window (its minute through its rounded end) belongs to the
//  prayer, not to any interval: work is scheduled between prayers, never on
//  top of them. An interval says what it is and how long it runs; how full it
//  turned out to be is never held against anybody (tests/helpers/shame.mjs).
//
//  DEGRADING HONESTLY. A device with no location set has no prayer times. The
//  answer is one unnamed interval covering the whole day — not a guessed
//  Fajr, not yesterday's times. The surface says what it is missing; this
//  module just refuses to invent structure it does not have.
// ════════════════════════════════════════════════════════════════════════════

(function (g: Record<string, unknown>) {
  'use strict';

  /** One stretch of schedulable time. `after` names the item that opened it
      (a prayer id, or 'sunrise'), null for the honest no-times interval. */
  interface MdScheduleInterval {
    id: string;
    label: string;
    startMin: number;
    endMin: number;
    after: string | null;
  }

  /** What an interval opener looks like on the wire from mdDayItems. */
  interface MdDayItem {
    id?: unknown;
    name?: unknown;
    min?: unknown;
    kind?: unknown;
    dur?: unknown;
  }

  const DAY_END = 1440;

  const ALL_DAY: MdScheduleInterval = Object.freeze(
    { id: 'interval-day', label: '', startMin: 0, endMin: DAY_END, after: null });

  /** An item that can open an interval: a prayer or sunrise, with a real
      minute and an id. Anything else is noise from a half-loaded day. */
  function opener(item: unknown): { id: string; label: string; min: number; windowEnd: number; prayer: boolean } | null {
    if (!item || typeof item !== 'object') return null;
    const it = item as MdDayItem;
    if (it.kind !== 'prayer' && it.kind !== 'sun') return null;
    if (typeof it.id !== 'string' || !it.id) return null;
    const min = Number(it.min);
    if (!Number.isFinite(min) || min < 0 || min >= DAY_END) return null;
    const dur = it.kind === 'prayer' && Number.isFinite(Number(it.dur)) ? Number(it.dur) : 0;
    const label = typeof it.name === 'string' && it.name ? it.name : it.id;
    return { id: it.id, label, min, windowEnd: min + Math.max(0, dur), prayer: it.kind === 'prayer' };
  }

  /** The day's intervals, in order. Each starts where its opener's window
      ends and runs to the next opener's minute (the gap between an interval's
      end and the next interval's start IS the next prayer's window). The last
      runs to the end of the clock day. */
  function mdScheduleIntervals(dayItems: unknown): MdScheduleInterval[] {
    const items = Array.isArray(dayItems)
      ? dayItems.map(opener).filter((o): o is NonNullable<ReturnType<typeof opener>> => o !== null)
      : [];
    // A day is only structured if it has prayers; sunrise alone proves nothing.
    if (!items.some((o) => o.prayer)) return [ALL_DAY];
    items.sort((a, b) => a.min - b.min);

    const out: MdScheduleInterval[] = [];
    for (let i = 0; i < items.length; i++) {
      const cur = items[i];
      const next = items[i + 1];
      if (!cur) continue;
      const startMin = cur.windowEnd;
      const endMin = next ? next.min : DAY_END;
      if (startMin >= endMin) continue; // a window that swallows its interval leaves nothing to schedule
      out.push({ id: 'interval-' + cur.id, label: cur.label, startMin, endMin, after: cur.id });
    }
    return out.length ? out : [ALL_DAY];
  }

  /** The one interval a minute falls inside, or null — a minute inside a
      prayer's window, or before the first interval, belongs to no interval. */
  function mdScheduleSlot(intervals: unknown, min: unknown): MdScheduleInterval | null {
    if (!Array.isArray(intervals)) return null;
    const m = Number(min);
    if (!Number.isFinite(m)) return null;
    for (const interval of intervals as MdScheduleInterval[]) {
      if (interval && m >= interval.startMin && m < interval.endMin) return interval;
    }
    return null;
  }

  g.mdScheduleIntervals = mdScheduleIntervals;
  g.mdScheduleSlot = mdScheduleSlot;

  // CommonJS escape hatch, following md-record.ts.
  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) {
    mod.exports = { mdScheduleIntervals, mdScheduleSlot };
  }
})(typeof window !== 'undefined' ? window : globalThis);
